import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
    AppBlockPort,
    AppBlockLinkParameter,
} from '@appblocks/codegeneration/types';
import * as micropython from './micropython';
import * as tios from './tios';
import * as simulator from './simulator';

export default class VariableChangedNode extends BaseNode {
    constructor() {
        super();
        this.name = 'On Variable Changed';
        this.icon = 'HiOutlineVariable';
        this.id = 'node_variable_changed';
        this.linkedFeature = 'general';

        this.outputs = [
            {
                name: 'Changed',
                portId: 'variable_changed',
                params: [] as AppBlockLinkParameter[],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'VARIABLE',
                name: 'Variable',
                settingType: SettingType.OPTION,
                refId: 'variable',
                required: true,
                default: '',
                options: [],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getOutputs(): AppBlockPort[] {
        const project: ProjectConfiguration = this.project;
        const feature = project.getFeature(this.getSettingValue('VARIABLE'));
        if (feature === undefined) {
            return this.outputs;
        }
        const DATATYPE = feature.getSettingValue('DATATYPE');

        const outputs = [
            {
                name: 'Changed',
                portId: 'variable_changed',
                params: [
                    {
                        name: 'value_old',
                        description: '',
                        dataType: DATATYPE,
                        default: '',
                    },
                ],
            },
        ];

        return outputs;
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const VARIABLE = project.getFeature(this.getSettingValue('VARIABLE'));
            if (VARIABLE) {
                return `{{$${VARIABLE.getSettingValue('ID')}}} changed`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const VARIABLE = project.getFeature(this.getSettingValue('VARIABLE'));
        if (VARIABLE) {
            return `{{$${VARIABLE.getSettingValue('ID')}}} changed`;
        }
        return this.name;
    }

    validate(): ValidationMessage[] {
        const errors: ValidationMessage[] = [];
        const project: ProjectConfiguration = this.project;
        const variable = this.getSettingValue('VARIABLE');
        if (variable === '') {
            return errors;
        }
        const chain: BaseNode[] = project.getOutputChain(this);
        for (let i = 0; i < chain.length; i++) {
            const node = chain[i];
            if (node.id === 'node_variable_set'
                && node.getSettingValue('VARIABLE') === variable) {
                errors.push({
                    message: 'Recursion not allowed',
                    messageType: ValidationMessageType.ERROR,
                    featureId: node.getID(),
                    settingId: 'VARIABLE',
                });
                break;
            }
        }

        return errors;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }


    getDetails(): string {
        return `
This block is triggered when the specified variable's value changes.`;
    }
}
